import {
	Alert,
	Badge,
	Box,
	Button,
	Col,
	Component,
	Divider,
	Dropdown,
	Grid,
	Heading,
	Icons,
	Inline,
	Select,
	Spinner,
	Stack,
	Table,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import {
	SectionCard,
	TDistributionPreferenceFormData,
} from "./AutoDistribution"
import {
	arrayMutators,
	Form,
	GetFieldValue,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { SelectUsers } from "../Users"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import * as Validator from "yup"
import { SelectTeams } from "../Teams"
import { useMemo, useState } from "react"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import {
	AutoDistributionXHR,
	TActiveBasedRuleSet,
	TAppliedRules,
	TDeviceType,
	TRuleSet,
} from "./store"
import useSWR from "swr"
import { GroupRuleSetField } from "./GroupRuleSetField"
import { extractRightOperandName } from "./utils"

type TDeviceOption = {
	value: TDeviceType
	name: string
}

export type TActiveBasedRuleSetFormValues = Omit<
	TActiveBasedRuleSet,
	"device_type"
> & {
	device_type?: {
		value: TDeviceType
		name: string
	}
}

const rulesSetSchema = Validator.object({
	device_type: Validator.mixed<TDeviceOption>().required(
		"Please select a device type"
	),

	all_users_included: Validator.boolean().nullable(),

	included_user: Validator.array(),

	included_team: Validator.array(),
})

const ExcludeActiveRulesSetSchema = Validator.object().shape({
	grouped_rule_items: Validator.array()
		.required("Rules are required")
		.min(1, "Select at least one rule")
		.of(
			Validator.array()
				.required("Sub Rules are required")
				.min(1, "Select at least one sub rule")
				.of(
					Validator.object().shape({
						left_operand_name: Validator.mixed()
							.nullable()
							.required("Condition  is required"),
						operator: Validator.mixed()
							.nullable()
							.required("Operator is required"),
						right_operand_value: Validator.mixed()
							.nullable()
							.required("Value is required"),
					})
				)
		),
	name: Validator.string().required("Rule name is required"),
})

const DEVICE_OPTIONS: TDeviceOption[] = [
	{
		value: "both",
		name: "Both devices",
	},
	{
		value: "mobile_only",
		name: "Mobile only",
	},
	{
		value: "desktop_only",
		name: "Desktop only",
	},
]

function getDeviceLabel(deviceType: TDeviceType) {
	return (
		DEVICE_OPTIONS.find((option) => option.value === deviceType)?.name ??
		deviceType
	)
}

type IActiveDistributionPreferencesProps = {
	name: string
	onSaveRuleSet: (editingIndex?: number | null) => Promise<boolean | undefined>
	onChange: () => Promise<boolean | undefined>
	deleteDeviceRule: (
		ruleSets: TActiveBasedRuleSet[]
	) => Promise<boolean | undefined>
}

export function ActiveUserDistributionPreferencesInputField({
	name,
	onSaveRuleSet,
	onChange,
	deleteDeviceRule,
}: IActiveDistributionPreferencesProps) {
	const form = useForm()

	const [isOpen, openDialog, closeDialog] = useDialog()
	const [editingIndex, setEditingIndex] = useState<number | null>(null)

	const { value: activeDistributionPreferences } =
		useFieldValue<
			TDistributionPreferenceFormData["preferences"]["active_user_distribution_preferences"]
		>(name)

	const { availableDeviceOptions, ruleSets } = useMemo(() => {
		const ruleSets = activeDistributionPreferences?.rule_set ?? []

		const usedDeviceTypes = new Set(
			ruleSets
				.filter((_, index) => index !== editingIndex)
				.map((rule) => rule.device_type)
		)

		const availableDeviceOptions = DEVICE_OPTIONS.filter(
			(option) => !usedDeviceTypes.has(option.value)
		)

		return { availableDeviceOptions, ruleSets }
	}, [activeDistributionPreferences?.rule_set, editingIndex])

	function handleAdd() {
		setEditingIndex(null)
		openDialog()
	}

	function handleEdit(index: number) {
		setEditingIndex(index)
		openDialog()
	}

	function handleClose() {
		setEditingIndex(null)
		closeDialog()
	}

	async function handleSave(ruleSet: TActiveBasedRuleSet) {
		const previousRuleSets = [...ruleSets]
		const updatedRuleSets = [...ruleSets]

		if (editingIndex === null) {
			updatedRuleSets.push(ruleSet)
		} else {
			updatedRuleSets[editingIndex] = ruleSet
		}

		form.change(
			"preferences.active_user_distribution_preferences.rule_set",
			updatedRuleSets
		)

		const hasNoError = await onSaveRuleSet(editingIndex)
		handleClose()

		// Restore previous rules if submission failed.
		if (!hasNoError) {
			form.change(
				"preferences.active_user_distribution_preferences.rule_set",
				previousRuleSets
			)
		}
	}

	async function handleDelete(index: number) {
		// Keep the current rule sets so we can restore them if submission fails.
		const previousRuleSets = ruleSets

		const updatedRuleSets = ruleSets.filter(
			(_, ruleIndex) => ruleIndex !== index
		)

		// Update the form immediately to reflect the deletion.
		form.change(
			"preferences.active_user_distribution_preferences.rule_set" as never,
			updatedRuleSets as never
		)

		// Submit the updated rule sets and check for backend errors.
		const hasNoError = await deleteDeviceRule(updatedRuleSets)

		if (!hasNoError) {
			// Restore the previous rule sets if submission fails.
			form.change(
				"preferences.active_user_distribution_preferences.rule_set" as never,
				previousRuleSets as never
			)
		}
	}

	return (
		<SectionCard
			title={
				<Inline alignItems="center" gap="2">
					Active User Distribution <Icons.NewBadge />
				</Inline>
			}
			description="Manage which users can receive leads based on their active device."
		>
			<Stack padding="4" width="full" bgColor="default" gap="6">
				{/* Enable */}
				<Box borderColor="default" borderWidth="1" rounded="md" padding="4">
					<SwitchInputField
						name="preferences.active_user_distribution_preferences.enabled"
						label="Enable active user distribution"
						onChange={async (value) => {
							const previousPreferences =
								form.getState().values.preferences
									?.active_user_distribution_preferences

							form.batch(() => {
								form.change(
									"preferences.active_user_distribution_preferences.enabled",
									value
								)
								form.change(
									"preferences.active_user_distribution_preferences.rule_set",
									[{ device_type: "both", all_users_included: true }]
								)
							})

							const hasNoError = await onChange()

							if (!hasNoError) {
								// Restore previous preferences if submission failed.
								form.batch(() => {
									form.change(
										"preferences.active_user_distribution_preferences.enabled",
										previousPreferences?.enabled
									)
									form.change(
										"preferences.active_user_distribution_preferences.rule_set",
										previousPreferences?.rule_set
									)
								})
							}
						}}
						help="Only currently active users will be eligible to receive new leads. Your existing lead assignment order remains unchanged."
					/>
				</Box>

				{activeDistributionPreferences?.enabled ? (
					<Stack gap="6">
						{/* Always active members */}
						<Inline
							borderColor="default"
							borderWidth="1"
							rounded="md"
							padding="4"
							justifyContent={"between"}
						>
							<SelectField
								select={SelectUsers}
								name="preferences.active_user_distribution_preferences.always_active_members"
								label="Always Active Members"
								multiple
								onChange={async (value, name) => {
									// getting prev value so that if error failed we can reset it
									const previousValue =
										form.getState().values.preferences
											?.active_user_distribution_preferences
											?.always_active_members

									form.change(name as never, value as never)
									const hasNoError = await onChange()

									if (!hasNoError) {
										// Restore previous rules if submission failed.
										form.change(name as never, previousValue as never)
									}
								}}
								help="These members will always be eligible to receive leads."
							/>
						</Inline>
						<ExceptionRuleSetList />

						{/* Rules */}
						<Stack gap="4">
							<Inline justifyContent="between" alignItems="center">
								<Stack gap="1">
									<Text fontWeight="semibold">Device Overrides</Text>

									<Text fontSize="sm" color="muted">
										Customize which devices are considered active for lead
										distribution.
									</Text>
								</Stack>

								<Button
									level="primary"
									disabled={availableDeviceOptions.length === 0}
									onClick={handleAdd}
								>
									<Icons.Plus />
									Add Rule
								</Button>
							</Inline>

							<Table
								bordered
								striped
								responsive
								headers={["Device", "Users", "Teams", "Scope", "Actions"]}
								rows={ruleSets.map((rule, index) => {
									const users =
										rule.included_user?.filter((user) => user.name) ?? []
									const teams =
										rule.included_team?.filter((team) => team.name) ?? []

									const visibleUsers = users.slice(0, 2)
									const remainingUsers = users.slice(2)

									const visibleTeams = teams.slice(0, 2)
									const remainingTeams = teams.slice(2)

									return [
										getDeviceLabel((rule.device_type ?? "both") as TDeviceType),

										rule.all_users_included ? (
											"All members"
										) : !users.length ? (
											"—"
										) : (
											<Inline gap="2" alignItems="center">
												{visibleUsers.map((user) => (
													<Badge key={user.id}>{user.name}</Badge>
												))}

												{remainingUsers.length > 0 && (
													<Tooltip
														content={remainingUsers
															.map((user) => user.name)
															.join(", ")}
													>
														<Text
															fontWeight="semibold"
															as="span"
															color={"success"}
														>
															+{remainingUsers.length}
														</Text>
													</Tooltip>
												)}
											</Inline>
										),

										rule.all_users_included ? (
											"All members"
										) : !teams.length ? (
											"—"
										) : (
											<Inline gap="2" alignItems="center">
												{visibleTeams.map((team) => (
													<Badge key={team.id}>{team.name}</Badge>
												))}

												{remainingTeams.length > 0 && (
													<Tooltip
														content={remainingTeams
															.map((team) => team.name)
															.join(", ")}
													>
														<Text
															fontWeight="semibold"
															as="span"
															color={"success"}
														>
															+{remainingTeams.length}
														</Text>
													</Tooltip>
												)}
											</Inline>
										),

										rule.all_users_included
											? "All members"
											: "Selected members",

										<Inline gap="2">
											<Button
												type="button"
												size="sm"
												onClick={() => handleEdit(index)}
											>
												Edit
											</Button>

											<Component initialState={false}>
												{({ state, setState }) => {
													return (
														<>
															<Button
																type="button"
																size="sm"
																onClick={() => setState(true)}
																status="danger"
																disabled={index <= 0}
															>
																Delete
															</Button>
															<Dialog
																open={state}
																onClose={() => setState(false)}
																sm
															>
																<Dialog.Header>
																	<Dialog.Title>
																		Are you sure you want to delete this device
																		rule?
																	</Dialog.Title>
																</Dialog.Header>
																<Dialog.Body>
																	<Text>
																		This action will permanently remove the
																		selected device rule.
																	</Text>
																</Dialog.Body>
																<Dialog.Footer>
																	<Stack gap="4">
																		<Inline gap="4">
																			<Button
																				onClick={() => {
																					handleDelete(index)
																					setState(false)
																				}}
																				type="submit"
																				status="warning"
																			>
																				Remove
																			</Button>
																			<Button
																				type="button"
																				onClick={() => setState(false)}
																			>
																				Cancel
																			</Button>
																		</Inline>
																	</Stack>
																</Dialog.Footer>
															</Dialog>
														</>
													)
												}}
											</Component>
										</Inline>,
									]
								})}
							/>

							{ruleSets.length === 0 && (
								<Box
									borderColor="default"
									borderWidth="1"
									rounded="md"
									padding="6"
								>
									<Stack alignItems="center" gap="2">
										<Text fontWeight="semibold">No device overrides</Text>

										<Text fontSize="sm" color="muted">
											All members currently follow the default "Both devices"
											behavior.
										</Text>
									</Stack>
								</Box>
							)}
						</Stack>
						<SubmissionError />
						{/* Default behavior */}
						<Alert>
							<Stack gap="2">
								<Text fontWeight="semibold">Default behavior</Text>

								<Text fontSize="sm" color="muted">
									By default, members are considered active when logged in from
									either mobile or desktop. Device overrides can be used to
									customize this behavior.
								</Text>

								<Inline alignItems={"center"} gap="2">
									<Text fontWeight="semibold">Both devices:</Text>

									<Text fontSize="sm" color="muted">
										Mobile or Desktop
									</Text>
								</Inline>
							</Stack>
						</Alert>

						<AddEditRuleSetDialog
							open={isOpen}
							onCancel={handleClose}
							onSuccess={handleSave}
							activeBasedRuleSet={
								editingIndex !== null ? ruleSets[editingIndex] : undefined
							}
							deviceOptions={availableDeviceOptions}
						/>
					</Stack>
				) : null}
			</Stack>
		</SectionCard>
	)
}

type TConfigureExceptionRuleSet = {
	grouped_rule_items: TRuleSet[][]
	name: string
}

type ConfigureExceptionRuleSetFormProps = {
	mode: "create" | "edit"
	initialValues: TConfigureExceptionRuleSet
	onSubmit: (values: TConfigureExceptionRuleSet) => Promise<void>
	onCancel: () => void
}

function ConfigureExceptionRuleSetForm({
	mode,
	initialValues,
	onSubmit,
	onCancel,
}: ConfigureExceptionRuleSetFormProps) {
	return (
		<Form<TConfigureExceptionRuleSet>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			validate={validateFormValues(ExcludeActiveRulesSetSchema)}
			mutators={{
				...arrayMutators,
			}}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="3">
						<TextInputField
							label="Enter Rule Name"
							name="name"
							type="text"
							required
						/>

						<GroupRuleSetField />

						<SubmissionError />

						<Divider marginY={"2"} />
						<Inline gap="4" justifyContent="end">
							<Button type="button" disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>

							<Button
								type="submit"
								status="primary"
								level="primary"
								disabled={submitting}
							>
								{submitting
									? "Saving..."
									: mode === "edit"
										? "Update Exceptions"
										: "Save Exceptions"}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function CreateExceptionRuleSet({
	onSuccess,
	onCancel,
}: {
	onSuccess?: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()

	async function handleSubmit(values: TConfigureExceptionRuleSet) {
		const grouped_rule_items = values.grouped_rule_items.map((group) =>
			group.map((rule) => ({
				left_operand_name: rule.left_operand_name?.name,
				right_operand_value: extractRightOperandName(rule.right_operand_value),
				operator: rule.operator?.name,
			}))
		)

		await xhr.post("/active-distribution-bypass-rules", {
			grouped_rule_items,
			name: values.name,
		})

		showSnackbar("Rule Set Created Successfully!")

		onSuccess?.()
	}

	return (
		<ConfigureExceptionRuleSetForm
			mode="create"
			initialValues={{
				grouped_rule_items: [
					[
						{
							left_operand_name: null,
							operator: null,
							right_operand_value: null,
							relation: "AND",
							type: "string",
						},
					],
				],
				name: "",
			}}
			onSubmit={handleSubmit}
			onCancel={onCancel}
		/>
	)
}

function EditExceptionRuleSet({
	activeBasedRuleSet,
	onSuccess,
	onCancel,
}: {
	activeBasedRuleSet: TAppliedRules["data"][number]
	onSuccess?: () => void | Promise<void>
	onCancel: () => void
}) {
	const xhr = useXHR()

	const initialValues = useMemo<TConfigureExceptionRuleSet>(
		() => ({
			grouped_rule_items:
				activeBasedRuleSet.grouped_rule_items?.map((ruleGroup) =>
					ruleGroup.map((rule, i, t) => {
						const { operator, ...rest } = rule.parameter

						return {
							left_operand_name: {
								...rest,
								name: rest.name,
							},
							operator,
							right_operand_value: rule.right_operand_value,
							relation: i === t.length - 1 ? "AND" : "OR",
							type: "string",
						}
					})
				) || [],
			name: activeBasedRuleSet.name || "",
		}),
		[activeBasedRuleSet]
	)
	const handleSubmit = async (values: TConfigureExceptionRuleSet) => {
		const grouped_rule_items = values.grouped_rule_items.map((group) =>
			group.map((rule) => ({
				left_operand_name: rule.left_operand_name?.name,
				right_operand_value: extractRightOperandName(rule.right_operand_value),
				operator: rule.operator?.name,
			}))
		)

		await xhr.patch(
			`/active-distribution-bypass-rules/${activeBasedRuleSet.id}`,
			{
				grouped_rule_items,
				name: values.name,
			}
		)

		await onSuccess?.()
		showSnackbar("Rule Set Updated Successfully!")
	}

	return (
		<ConfigureExceptionRuleSetForm
			mode="edit"
			initialValues={initialValues}
			onSubmit={handleSubmit}
			onCancel={onCancel}
		/>
	)
}

function AddEditExceptionRuleSetForm({
	activeBasedRuleSet,
	onSuccess,
	onCancel,
}: {
	activeBasedRuleSet?: TAppliedRules["data"][0]
	onSuccess?: () => void | Promise<void>
	onCancel: () => void
}) {
	return activeBasedRuleSet ? (
		<EditExceptionRuleSet
			activeBasedRuleSet={activeBasedRuleSet}
			onSuccess={onSuccess}
			onCancel={onCancel}
		/>
	) : (
		<CreateExceptionRuleSet onSuccess={onSuccess} onCancel={onCancel} />
	)
}

function ConfigureExceptionRuleSetDialog({
	onCancel,
	open,
	activeBasedRuleSet,
	onSuccess,
}: {
	open: boolean
	onCancel: () => void
	activeBasedRuleSet?: TAppliedRules["data"][0]
	onSuccess?: () => void | Promise<void>
}) {
	return (
		<Dialog
			open={open}
			onClose={onCancel}
			title={
				<Stack gap="2">
					<Heading fontSize="lg">Configure Active User Exceptions</Heading>

					<Text fontSize="sm" fontWeight="normal" color="muted">
						Members selected in the matching rule will remain eligible to
						receive leads, even if they are inactive.
					</Text>
				</Stack>
			}
		>
			<Dialog.Body>
				<AddEditExceptionRuleSetForm
					activeBasedRuleSet={activeBasedRuleSet}
					onSuccess={onSuccess}
					onCancel={onCancel}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function validateRuleSet(
	ruleSet: TActiveBasedRuleSetFormValues
): TActiveBasedRuleSet {
	if (
		!ruleSet.all_users_included &&
		!ruleSet.included_team.length &&
		!ruleSet.included_user.length
	) {
		throw new Error("Please select at least one user or team")
	}

	return {
		...ruleSet,
		device_type: ruleSet.device_type?.value,
	}
}

function RuleSetForm({
	initialValues,
	onSubmit,
	onCancel,
	deviceOptions,
	mode = "create",
}: {
	initialValues: TActiveBasedRuleSetFormValues
	onSubmit: (values: TActiveBasedRuleSetFormValues) => Promise<void>
	onCancel: () => void
	deviceOptions: TDeviceOption[]
	mode?: "create" | "edit"
}) {
	return (
		<Form<TActiveBasedRuleSetFormValues>
			initialValues={initialValues}
			onSubmit={withServerErrors(onSubmit)}
			validate={validateFormValues(rulesSetSchema)}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => {
				return (
					<form onSubmit={handleSubmit} noValidate>
						<Stack gap="6">
							<SelectField
								name="device_type"
								select={Select}
								options={deviceOptions}
								label="Active device"
								labelKey="name"
								secondaryLabel="Select the device from which the member must be logged in to be considered active."
								disabled={mode === "edit"}
							/>

							<SwitchInputField
								label="Apply this rule to all members"
								name="all_users_included"
								help="When enabled, this device rule will apply to all members."
							/>

							<GetFieldValue name="all_users_included">
								{({ value: isAllSelected }) => {
									if (isAllSelected) {
										return <Alert>This rule will apply to all members.</Alert>
									}

									return (
										<Grid noGutters>
											<Col xs={12} md={6}>
												<SelectField
													select={SelectUsers}
													name="included_user"
													label="Select Users"
													multiple
													fetchOnMount
													maxWidth={"xs"}
												/>
											</Col>
											<Col xs={12} md={6}>
												<SelectField
													select={SelectTeams}
													name="included_team"
													label="Select Teams"
													multiple
													fetchOnMount
													maxWidth={"xs"}
												/>
											</Col>
										</Grid>
									)
								}}
							</GetFieldValue>

							<SubmissionError />
							<Divider marginY={"2"} />
							<Inline gap="4" justifyContent="end">
								<Button type="button" disabled={submitting} onClick={onCancel}>
									Cancel
								</Button>

								<Button
									type="submit"
									status="primary"
									level="primary"
									disabled={submitting}
								>
									{submitting ? "Saving..." : "Save Rule"}
								</Button>
							</Inline>
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

function AddEditRuleSetForm({
	activeBasedRuleSet,
	deviceOptions,
	onCancel,
	onSuccess,
}: {
	activeBasedRuleSet?: TActiveBasedRuleSet
	deviceOptions: TDeviceOption[]
	onCancel: () => void
	onSuccess: (values: TActiveBasedRuleSet) => void | Promise<void>
}) {
	const initialFormValues = useMemo<TActiveBasedRuleSetFormValues>(
		() =>
			activeBasedRuleSet
				? {
						...activeBasedRuleSet,
						device_type: activeBasedRuleSet.device_type
							? deviceOptions.find(
									(option) => option.value === activeBasedRuleSet.device_type
								)
							: undefined,
						included_team: activeBasedRuleSet.included_team ?? [],
						included_user: activeBasedRuleSet.included_user ?? [],
					}
				: {
						device_type: undefined,
						all_users_included: true,
						included_user: [],
						included_team: [],
					},
		[activeBasedRuleSet, deviceOptions]
	)

	return activeBasedRuleSet ? (
		<RuleSetForm
			deviceOptions={deviceOptions}
			initialValues={initialFormValues}
			onCancel={onCancel}
			onSubmit={async (values) => {
				const validatedValues = validateRuleSet(values)
				await onSuccess(validatedValues)
			}}
			mode="edit"
		/>
	) : (
		<RuleSetForm
			deviceOptions={deviceOptions}
			initialValues={initialFormValues}
			onCancel={onCancel}
			onSubmit={async (values) => {
				const validatedValues = validateRuleSet(values)

				await onSuccess(validatedValues)
			}}
		/>
	)
}

function AddEditRuleSetDialog({
	onSuccess,
	onCancel,
	open,
	activeBasedRuleSet,
	deviceOptions,
}: {
	open: boolean
	onCancel: () => void
	onSuccess: (values: TActiveBasedRuleSet) => void
	activeBasedRuleSet?: TActiveBasedRuleSet
	deviceOptions: TDeviceOption[]
}) {
	return (
		<Dialog
			open={open}
			onClose={onCancel}
			title={
				<Stack gap="2">
					<Heading fontSize="lg">
						{activeBasedRuleSet ? "Edit Device Rule" : "Add Device Rule"}
					</Heading>

					<Text fontSize="sm" fontWeight="normal" color="muted">
						Choose which members should be considered active on a specific
						device.
					</Text>
				</Stack>
			}
		>
			<Dialog.Body>
				<AddEditRuleSetForm
					activeBasedRuleSet={activeBasedRuleSet}
					deviceOptions={deviceOptions}
					onCancel={onCancel}
					onSuccess={onSuccess}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function AddRuleGroupButton({
	title,
	activeBasedRuleSet,
	onSuccess,
	isEditing,
}: {
	title?: string
	activeBasedRuleSet?: TAppliedRules["data"][0]
	onSuccess: () => void | Promise<void>
	isEditing?: boolean
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			<Button
				onClick={() => {
					openDialog()
				}}
			>
				<Inline alignItems={"center"} gap={"1"}>
					{isEditing ? <Icons.Pencil /> : <Icons.Plus />}{" "}
					{title || "Add Rule Set"}
				</Inline>
			</Button>

			<ConfigureExceptionRuleSetDialog
				open={isOpen}
				onCancel={() => {
					closeDialog()
				}}
				onSuccess={async () => {
					await onSuccess()
					closeDialog()
				}}
				activeBasedRuleSet={activeBasedRuleSet}
			/>
		</>
	)
}

function ExceptionRuleSetList() {
	const xhr = useXHR()

	const {
		data: appliedRules,
		isLoading,
		mutate: refresh,
	} = useSWR("/active-distribution-bypass-rules", () =>
		AutoDistributionXHR(xhr).getBypassRules({})
	)
	const [isOpenRuleSet, openRuleSetDialog, closeRuleSetDialog] = useDialog()

	if (isLoading) return <Spinner padding="4" alignCenter />

	return (
		<Stack
			gap={"4"}
			padding="4"
			borderColor="default"
			borderWidth="1"
			rounded="md"
		>
			<Inline justifyContent="between" alignItems="center">
				<Stack gap="1">
					<Text fontWeight="semibold">Rule Set Exceptions</Text>

					<Text fontSize="sm" color="muted">
						Define rule sets where the active distribution rule is ignored when
						the specified conditions match.
					</Text>
				</Stack>

				<Button onClick={openRuleSetDialog} status="primary">
					Add Exception Rule Set
				</Button>
			</Inline>
			{!appliedRules?.data?.length ? (
				<Stack
					borderColor="default"
					borderWidth="1"
					alignItems={"center"}
					gap="1"
					padding="4"
					rounded="md"
				>
					<Text fontWeight="semibold">No exception rule sets added</Text>
					<Text fontSize={"sm"} color={"muted"}>
						Add an exception rule set to skip the active-user check for specific
						users or teams.
					</Text>
				</Stack>
			) : (
				<Table
					bordered
					striped
					headers={["Rule Set Name", "Exception Rules", ""]}
					rows={appliedRules.data.map((ruleGroup) => [
						<Inline gap="3">{ruleGroup.name}</Inline>,
						<Stack alignItems={"stretch"} justifyContent={"center"}>
							{ruleGroup.grouped_rule_items.map((group, gi) => (
								<Stack key={gi}>
									<Box
										borderColor={"default"}
										borderWidth={"1"}
										bgColor={"accent"}
										padding={"2"}
										rounded="md"
									>
										{group.map((singleRule, si) => (
											<Text key={singleRule.id}>
												{si ? (
													<Box
														zIndex="100"
														fontWeight="semibold"
														borderColor={"default"}
														borderWidth={"1"}
														fontSize={"xs"}
														as="span"
														padding={"1"}
														bgColor={"subtle"}
														rounded="md"
													>
														OR
													</Box>
												) : (
													""
												)}{" "}
												{singleRule.parameter.display_name}{" "}
												<strong>{singleRule.parameter.operator.name}</strong>{" "}
												{Array.isArray(singleRule.right_operand_value)
													? singleRule.right_operand_value.join(", ")
													: singleRule.right_operand_value}
											</Text>
										))}
									</Box>

									{ruleGroup.grouped_rule_items.length - 1 !== gi && (
										<Box textAlign={"center"}>
											<Inline
												alignItems={"stretch"}
												justifyContent={"center"}
												height={"12"}
												gap={"2"}
												position="relative"
											>
												<Box
													position="absolute"
													top="0"
													bottom="0"
													style={{ width: "1px", zIndex: -1 }}
													backgroundColor={"emphasis"}
													opacity="50"
												/>

												<Stack justifyContent={"center"}>
													<Box
														fontWeight="semibold"
														borderColor={"default"}
														borderWidth={"1"}
														fontSize={"xs"}
														as="span"
														padding={"1"}
														bgColor={"subtle"}
														rounded="md"
													>
														AND
													</Box>
												</Stack>
											</Inline>
										</Box>
									)}
								</Stack>
							))}
						</Stack>,
						<Inline justifyContent={"end"}>
							<Dropdown alignRight alignItems={"end"}>
								<Button level="tertiary" as={Dropdown.Toggle} size="sm">
									<Icons.DotsVertical />
								</Button>
								<Dropdown.Menu>
									<AddRuleGroupButton
										onSuccess={() => {
											refresh()
										}}
										activeBasedRuleSet={ruleGroup}
										title="Edit"
										isEditing={true}
									/>
									<Component initialState={false}>
										{({ state, setState }) => {
											return (
												<>
													<Button onClick={() => setState(true)}>
														<Inline alignItems={"center"}>
															<Icons.Trash /> Delete
														</Inline>
													</Button>

													<Dialog
														open={state}
														onClose={() => setState(false)}
														sm
													>
														<Dialog.Header>
															<Dialog.Title>Delete Rule Set</Dialog.Title>
														</Dialog.Header>
														<Form<unknown>
															onSubmit={withServerErrors(async () => {
																const resp = await xhr.delete<{
																	message: string
																}>(
																	`/active-distribution-bypass-rules/${ruleGroup.id}`
																)
																refresh()
																setState(false)
																showSnackbar(resp.data.message)
															})}
															subscription={{
																submitting: true,
															}}
														>
															{({ submitting, handleSubmit }) => (
																<form noValidate onSubmit={handleSubmit}>
																	<Dialog.Body>
																		<Text>
																			This action will permanently remove the
																			selected rule set.
																		</Text>
																	</Dialog.Body>
																	<Dialog.Footer>
																		<Stack gap="4">
																			<SubmissionError />
																			<Inline gap="4">
																				<Button
																					type="submit"
																					disabled={submitting}
																					status="warning"
																				>
																					{submitting
																						? "deleting..."
																						: "I understand, delete it."}
																				</Button>
																				<Button
																					onClick={() => setState(false)}
																					disabled={submitting}
																				>
																					Cancel
																				</Button>
																			</Inline>
																		</Stack>
																	</Dialog.Footer>
																</form>
															)}
														</Form>
													</Dialog>
												</>
											)
										}}
									</Component>
								</Dropdown.Menu>
							</Dropdown>
						</Inline>,
					])}
				/>
			)}

			<ConfigureExceptionRuleSetDialog
				open={isOpenRuleSet}
				onCancel={closeRuleSetDialog}
				onSuccess={() => {
					refresh()
					closeRuleSetDialog()
				}}
			/>
		</Stack>
	)
}
